import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { CodigoArticulo } from '../components/CodigoArticulo';
import { textoArticulo, useIdentidades } from '../lib/identidadArticulos';

interface BomItem {
  skuPadre: string;
  skuHijo: string;
  cantidad: string;
  hijo?: { skuInterno: string; descripcion?: string; unidadMedidaBase?: string };
}

/** Recursive BOM tree node. */
interface BomTreeNode {
  sku: string;
  cantidad: number;
  descripcion: string | null;
  unidad: string | null;
  ciclico?: boolean;
  hijos?: BomTreeNode[];
}

function BomTreeRow({ node, depth }: { node: BomTreeNode; depth: number }) {
  return (
    <>
      <tr className={node.ciclico ? 'text-amber-600' : ''}>
        <td className="py-1" style={{ paddingLeft: `${depth * 1.5 + 0.5}rem` }}>
          <span className="text-slate-300 mr-1">{depth > 0 ? '└' : ''}</span>
          <span className="text-xs font-medium"><CodigoArticulo sku={node.sku} /></span>
          {node.ciclico && <span className="text-amber-500 text-xs ml-1">(ciclo)</span>}
        </td>
        <td className="py-1 text-xs text-slate-500">{node.descripcion || '—'}</td>
        <td className="py-1 text-xs text-right tabular-nums">{node.cantidad}</td>
        <td className="py-1 text-xs text-slate-400">{node.unidad || '—'}</td>
      </tr>
      {node.hijos?.map((h, i) => (
        <BomTreeRow key={`${h.sku}-${i}`} node={h} depth={depth + 1} />
      ))}
    </>
  );
}

/** Inline-editable cantidad cell. */
function CantidadCell({ row, onUpdate }: { row: BomItem; onUpdate: () => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.cantidad);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { can } = useAuth();

  const handleSave = async () => {
    const parsed = Number(value);
    if (isNaN(parsed) || parsed <= 0) {
      toast.addToast('La cantidad debe ser un número positivo', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/api/v3/bom/${row.skuPadre}/${row.skuHijo}`, { cantidad: parsed });
      toast.addToast('Cantidad actualizada', 'success');
      onUpdate();
    } catch {
      toast.addToast('Error al actualizar', 'error');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          min="0.01"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          disabled={saving}
          className="w-16 px-1 py-0.5 text-xs border rounded"
          autoFocus
        />
        <button onClick={handleSave} disabled={saving} className="text-green-600 hover:text-green-800 text-xs">✓</button>
        <button onClick={() => { setEditing(false); setValue(row.cantidad); }} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
      </div>
    );
  }

  return (
    <button
      onClick={() => { if (can('bom', 'editar')) { setEditing(true); setValue(row.cantidad); } }}
      disabled={!can('bom', 'editar')}
      title={can('bom', 'editar') ? 'Click para editar cantidad' : 'No tenés permiso para editar'}
      className="text-left text-xs tabular-nums hover:bg-slate-50 rounded px-1 py-0.5 -mx-1 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {row.cantidad}
    </button>
  );
}

export function BomPage() {
  const { data, loading, refetch } = useApi<BomItem[]>('/api/v3/bom');
  const [filters, setFilters] = useState<Record<string, string>>({});
  // Codes (what users see and filter by) and the component's identity supplier, also for the CSV/XLSX export.
  const identidad = useIdentidades((data ?? []).flatMap(r => [r.skuPadre, r.skuHijo]));
  const filas = (data ?? []).map(r => ({
    ...r,
    codigoPadre: textoArticulo(r.skuPadre, identidad(r.skuPadre)),
    codigoHijo: textoArticulo(r.skuHijo, identidad(r.skuHijo)),
    proveedorArticulo: identidad(r.skuHijo)?.proveedor?.nombre ?? '',
  }));
  const filtered = filas.filter(row => Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())));

  const toast = useToast();
  const { can } = useAuth();

  // BOM tree modal
  const [treeSku, setTreeSku] = useState<string | null>(null);
  const [treeOpen, setTreeOpen] = useState(false);
  const [treeData, setTreeData] = useState<BomTreeNode | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);

  const openTree = async (sku: string) => {
    setTreeSku(sku);
    setTreeOpen(true);
    setTreeLoading(true);
    try {
      const data = await api.get<BomTreeNode>(`/api/v3/bom/${sku}/arbol`);
      setTreeData(data);
    } catch {
      toast.addToast('Error al cargar el árbol BOM', 'error');
    } finally {
      setTreeLoading(false);
    }
  };

  const handleDelete = async (padre: string, hijo: string) => {
    try {
      await api.delete(`/api/v3/bom/${padre}/${hijo}`);
      toast.addToast('Eliminado', 'success');
      refetch();
    } catch {
      toast.addToast('Error', 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">BOM — Lista de Materiales</h1>
          <HelpTip tips={[
            'Bill of Materials: productos compuestos por otros SKU',
            'SKU Padre = producto final, SKU Hijo = componente',
            'La cantidad indica cuántas unidades del hijo lleva el padre',
            'Click en "Ver" para explorar el árbol completo de componentes',
            'Click en la cantidad para editarla directamente',
            'Para crear componentes BOM nuevos usá Insumos — ahí se crean los SKU faltantes automáticamente',
          ]} />
          <p className="text-slate-500 text-sm mt-1">Estructura de productos compuestos</p>
        </div>
      </div>

      <DataTable
        columns={[
          { key: 'codigoPadre', header: 'Código padre', render: (r: BomItem) => <CodigoArticulo sku={r.skuPadre} /> },
          { key: 'codigoHijo', header: 'Código hijo', render: (r: BomItem) => <CodigoArticulo sku={r.skuHijo} /> },
          { key: 'proveedorArticulo', header: 'Proveedor (hijo)' },
          { key: 'hijo', header: 'Descripción Hijo', render: (r: BomItem) => r.hijo?.descripcion || '—', filterable: false },
          {
            key: 'cantidad',
            header: 'Cant.',
            render: (r: BomItem) => <CantidadCell row={r} onUpdate={refetch} />,
          },
          { key: 'hijo', header: 'UM', render: (r: BomItem) => r.hijo?.unidadMedidaBase || '—', filterable: false },
          {
            key: 'actions',
            header: '',
            render: (r: BomItem) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); openTree(r.skuPadre); }}
                  title="Ver árbol completo de componentes"
                  className="text-blue-600 hover:text-blue-800 text-xs"
                >
                  Ver
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(r.skuPadre, r.skuHijo); }}
                  disabled={!can('bom', 'eliminar')}
                  title={!can('bom', 'eliminar') ? 'No tenés permiso para eliminar' : 'Eliminar componente'}
                  className="text-red-600 hover:text-red-800 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Eliminar
                </button>
              </div>
            ),
            filterable: false,
          },
        ]}
        data={filtered}
        loading={loading}
        filters={filters}
        onFilterChange={(k, v) => setFilters(f => v ? { ...f, [k]: v } : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)))}
      />

      {/* BOM tree modal */}
      <Modal open={treeOpen} onClose={() => { setTreeOpen(false); setTreeData(null); }} title={`Árbol BOM: ${treeSku ? textoArticulo(treeSku, identidad(treeSku)) : ''}`} size="lg">
        {treeLoading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Cargando árbol...</p>
        ) : treeData ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-slate-400 uppercase">
                  <th className="py-2 font-medium">SKU</th>
                  <th className="py-2 font-medium">Descripción</th>
                  <th className="py-2 font-medium text-right">Cant.</th>
                  <th className="py-2 font-medium">UM</th>
                </tr>
              </thead>
              <tbody>
                <BomTreeRow node={treeData} depth={0} />
              </tbody>
            </table>
            {treeData.ciclico && (
              <p className="text-xs text-amber-500 mt-2">⚠ Este SKU forma parte de un ciclo en la BOM.</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-400 py-8 text-center">Sin datos</p>
        )}
      </Modal>
    </div>
  );
}
